import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { fmtIDR, fmtDateTime, firstOfMonthISO, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Receipt, Printer, RotateCcw, Search, Ban, CheckCircle2 } from "lucide-react";

const buildReceiptHtml = (inv) => {
  const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");
  const productsTotal = (inv.product_items || []).reduce((s, p) => s + Number(p.unit_price || 0) * Number(p.quantity || 0), 0);
  const treatmentItems = inv.treatment_items || [];
  const computedSub = Number(inv.amount || 0) + Number(inv.discount_amount || 0) - Number(inv.shipping_cost || 0);
  const subtotal = Number(inv.subtotal) > 0 ? Number(inv.subtotal) : computedSub;
  const treatmentAmount = subtotal - productsTotal;
  const fmtWaktu = (val) => {
    const d = val ? new Date(val) : new Date();
    if (isNaN(d.getTime())) return String(val || "").slice(0, 16).replace("T", " ");
    return d.toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };
  const rows = [];
  if (treatmentItems.length) {
    treatmentItems.forEach((t) => {
      const q = Number(t.quantity || 1);
      rows.push(`<tr><td>${t.name || "Treatment"}${q > 1 ? ` x${q}` : ""}</td><td class="r">${rp(Number(t.unit_price || 0) * q)}</td></tr>`);
    });
  } else if (inv.treatment_name) {
    rows.push(`<tr><td>${inv.treatment_name}</td><td class="r">${rp(treatmentAmount)}</td></tr>`);
  }
  (inv.product_items || []).forEach((p) => {
    rows.push(`<tr><td>${p.name || "Produk"} x${Number(p.quantity || 1)}</td><td class="r">${rp(Number(p.unit_price || 0) * Number(p.quantity || 0))}</td></tr>`);
  });
  let payLines = `<div class="line"><span>Metode</span><span>${(inv.payment_method || "").toUpperCase()}</span></div>`;
  if (inv.payments && inv.payments.length > 1) {
    payLines = `<div class="lbl">Pembayaran (Split):</div>` + inv.payments.map((p) => `<div class="line"><span>• ${(p.method || "").toUpperCase()}</span><span>${rp(p.amount)}</span></div>`).join("");
  }
  return `<!doctype html><html><head><meta charset="utf-8"><title>Struk ${inv.invoice_no || ""}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  body { width: 80mm; margin: 0; padding: 10px 12px; font-family: 'Courier New', monospace; font-size: 12px; color: #000; }
  .c { text-align: center; } .b { font-weight: 700; }
  .name { font-size: 16px; font-weight: 700; letter-spacing: .5px; }
  .muted { font-size: 10px; color: #333; line-height: 1.35; }
  .inv { text-align:center; font-size: 11px; font-weight:700; margin-top:2px; }
  hr { border: none; border-top: 1px dashed #000; margin: 7px 0; }
  .meta { font-size: 11px; } .meta .line { margin: 1px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 2px 0; vertical-align: top; font-size: 11px; }
  td.r { text-align: right; white-space: nowrap; padding-left: 6px; }
  .line { display: flex; justify-content: space-between; font-size: 11px; }
  .lbl { font-size: 11px; margin-top: 2px; font-weight: 700; }
  .total { display: flex; justify-content: space-between; font-size: 15px; font-weight: 700; margin-top: 4px; border-top: 2px solid #000; padding-top: 4px; }
  .foot { text-align: center; font-size: 10px; margin-top: 10px; }
</style></head><body onload="window.print(); setTimeout(function(){window.close();}, 300);">
  <div class="c name">${inv.clinic_name || "MyClinic"}</div>
  ${inv.branch_name ? `<div class="c muted b">${inv.branch_name}</div>` : ""}
  ${inv.clinic_address ? `<div class="c muted">${inv.clinic_address}</div>` : ""}
  <div class="inv">INVOICE ${inv.invoice_no || "-"}</div>
  <hr/>
  <div class="meta">
    <div class="line"><span>Tgl Transaksi</span><span>${fmtWaktu(inv.paid_at)} WIB</span></div>
    <div class="line"><span>Pelanggan</span><span>${inv.patient_name || "-"}</span></div>
  </div>
  <hr/>
  <div class="lbl">Rincian:</div>
  <table>${rows.join("")}</table>
  <hr/>
  <div class="line"><span>Subtotal</span><span>${rp(subtotal)}</span></div>
  ${Number(inv.discount_amount) > 0 ? `<div class="line"><span>Diskon</span><span>-${rp(inv.discount_amount)}</span></div>` : ""}
  ${Number(inv.shipping_cost) > 0 ? `<div class="line"><span>Ongkir</span><span>+${rp(inv.shipping_cost)}</span></div>` : ""}
  <div class="total"><span>GRAND TOTAL</span><span>${rp(inv.amount)}</span></div>
  <hr/>
  ${payLines}
  <div class="foot">${inv.invoice_footer || "Terima kasih atas kunjungan Anda."}</div>
</body></html>`;
};

const itemSummary = (t) => {
  const parts = [];
  (t.treatment_items || []).forEach((ti) => parts.push(`${ti.name || "Treatment"}${Number(ti.quantity) > 1 ? ` x${ti.quantity}` : ""}`));
  (t.product_items || []).forEach((pi) => parts.push(`${pi.name || "Produk"}${Number(pi.quantity) > 1 ? ` x${pi.quantity}` : ""}`));
  return parts.length ? parts.join(", ") : "—";
};

export default function TransactionHistory() {
  const { branchId } = useBranch();
  const { user } = useAuth();
  const canRefund = ["owner", "manager", "super_admin"].includes(user?.role);

  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());

  const [refundTarget, setRefundTarget] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  const [refunding, setRefunding] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/transactions", { params: { branch_id: branchId } })
      .then((r) => setTxs(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [branchId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return txs.filter((t) => {
      const d = (t.paid_at || "").slice(0, 10);
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      if (q && !((t.patient_name || "").toLowerCase().includes(q) || (t.patient_phone || "").includes(q))) return false;
      return true;
    });
  }, [txs, search, dateFrom, dateTo]);

  const totals = useMemo(() => {
    const active = filtered.filter((t) => t.status !== "refunded");
    return {
      count: active.length,
      revenue: active.reduce((s, t) => s + Number(t.amount || 0), 0),
      refunds: filtered.filter((t) => t.status === "refunded").length,
    };
  }, [filtered]);

  const printTxById = async (txId) => {
    try {
      const { data: inv } = await api.get(`/transactions/${txId}/invoice`);
      const w = window.open("", "_blank", "width=380,height=640");
      if (!w) { toast.error("Popup diblokir browser. Izinkan popup untuk mencetak struk."); return; }
      w.document.write(buildReceiptHtml(inv));
      w.document.close();
    } catch {
      toast.error("Gagal memuat struk transaksi");
    }
  };

  const openRefund = (t) => { setRefundTarget(t); setRefundReason(""); };
  const doRefund = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      await api.post(`/transactions/${refundTarget.id}/refund`, { reason: refundReason || null });
      toast.success("Transaksi dibatalkan. Stok, deposit & laporan telah dikembalikan.");
      setRefundTarget(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membatalkan transaksi");
    } finally {
      setRefunding(false);
    }
  };

  const applyPreset = (kind) => {
    const now = new Date();
    if (kind === "this_month") { setDateFrom(firstOfMonthISO()); setDateTo(todayISO()); }
    else if (kind === "last_month") {
      const f = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const l = new Date(now.getFullYear(), now.getMonth(), 0);
      setDateFrom(f.toISOString().slice(0, 10)); setDateTo(l.toISOString().slice(0, 10));
    } else if (kind === "7d") {
      const f = new Date(now); f.setDate(f.getDate() - 6);
      setDateFrom(f.toISOString().slice(0, 10)); setDateTo(todayISO());
    } else if (kind === "30d") {
      const f = new Date(now); f.setDate(f.getDate() - 29);
      setDateFrom(f.toISOString().slice(0, 10)); setDateTo(todayISO());
    }
  };

  return (
    <div className="space-y-6" data-testid="tx-history-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Riwayat Transaksi</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Semua transaksi penjualan. {canRefund ? "Batalkan/refund transaksi untuk mengembalikan stok, deposit & laporan." : "Refund hanya untuk Manager/Owner."}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="tx-history-search" className="pl-9" placeholder="Cari nama / no HP pelanggan..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Dari</Label>
            <Input type="date" data-testid="tx-history-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Sampai</Label>
            <Input type="date" data-testid="tx-history-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => applyPreset("this_month")}>Bulan Ini</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("last_month")}>Bulan Lalu</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("7d")}>7 Hari</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("30d")}>30 Hari</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Transaksi Aktif</div><div className="font-heading text-2xl" data-testid="tx-history-count">{totals.count}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Total Penjualan (net)</div><div className="font-heading text-2xl" data-testid="tx-history-revenue">{fmtIDR(totals.revenue)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Dibatalkan</div><div className="font-heading text-2xl text-rose-600" data-testid="tx-history-refunds">{totals.refunds}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Receipt className="h-5 w-5" /> Daftar Transaksi</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Waktu</TableHead>
                <TableHead>Pelanggan</TableHead>
                <TableHead>Rincian</TableHead>
                <TableHead>Metode</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">Memuat...</TableCell></TableRow>}
              {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">Tidak ada transaksi pada periode ini.</TableCell></TableRow>}
              {!loading && filtered.map((t) => {
                const refunded = t.status === "refunded";
                return (
                  <TableRow key={t.id} data-testid={`tx-row-${t.id}`} className={refunded ? "opacity-60" : ""}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(t.paid_at)}</TableCell>
                    <TableCell className="text-sm font-medium">{t.patient_name || "—"}</TableCell>
                    <TableCell className="text-xs max-w-[240px] truncate" title={itemSummary(t)}>{itemSummary(t)}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs uppercase">{t.payment_method}</Badge></TableCell>
                    <TableCell className={`text-right text-sm font-medium ${refunded ? "line-through" : ""}`}>{fmtIDR(t.amount)}</TableCell>
                    <TableCell>
                      {refunded ? (
                        <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 text-[10px]" title={t.refund_reason || ""} data-testid={`tx-status-${t.id}`}><Ban className="h-2.5 w-2.5 mr-0.5" /> Dibatalkan</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]" data-testid={`tx-status-${t.id}`}><CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Selesai</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" data-testid={`tx-print-${t.id}`} onClick={() => printTxById(t.id)} title="Cetak struk">
                          <Printer className="h-3.5 w-3.5" />
                        </Button>
                        {canRefund && !refunded && (
                          <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`tx-refund-${t.id}`} onClick={() => openRefund(t)} title="Batalkan / Refund">
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Refund dialog */}
      <Dialog open={!!refundTarget} onOpenChange={(o) => !o && setRefundTarget(null)}>
        <DialogContent className="max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2 text-rose-600"><RotateCcw className="h-5 w-5" /> Batalkan / Refund Transaksi</DialogTitle>
            <DialogDescription>
              Transaksi <b>{fmtIDR(refundTarget?.amount || 0)}</b> atas nama <b>{refundTarget?.patient_name || "-"}</b> akan dibatalkan.
              Stok produk, saldo deposit, dan total belanja pelanggan akan dikembalikan; transaksi ini dikeluarkan dari seluruh laporan penjualan & P&L. Tindakan ini tidak dapat dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Alasan Pembatalan</Label>
            <Textarea data-testid="refund-reason-input" rows={3} value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Contoh: salah input, pelanggan batal, dsb." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTarget(null)}>Batal</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" data-testid="refund-confirm-btn" disabled={refunding} onClick={doRefund}>
              {refunding ? "Memproses..." : "Ya, Batalkan Transaksi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
